import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SparePart, SpareParts } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { SparePartService } from '@ecs-services/spare-part/spare-part.service';
import { TranslatePipe } from '@ngx-translate/core';
import { OnDestroyBase, WINDOW, ensure, roundFloatingNumber } from '@os-components';
import { SewButton, SewInput, SewSkeleton } from '@sew/ui-components-angular/standalone';
import { takeUntil } from 'rxjs/operators';
import { BasketItemType } from '../../../../../services/basket.service';
import { PartAmounts } from '../../../../part-table/part-table.component';
import { InjectionDataStep, StepType, WIZARD_STEP_TOKEN } from '../../part-configuration-wizard.component';
import { StoreStepItems } from '../../part-configuration-wizard.service';
import {
    CableErrorType,
    CrossSellingCableStep,
    SparePartWithCableSteps,
    SparePartsWithCableSteps,
} from './cable-configuration.interface';

export interface CableConfigurationData {
    type: StepType.CABLE;
    parts: SpareParts;
    partAmounts: PartAmounts;
}

@Component({
    selector: 'ecs-cable-configuration',
    templateUrl: './cable-configuration.component.html',
    styleUrls: ['./cable-configuration.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        TranslatePipe,
        SewButton,
        SewInput,
        SewSkeleton,
    ],
})
export class CableConfigurationComponent extends OnDestroyBase implements OnInit, OnDestroy {
    public parts: SparePartsWithCableSteps = [];
    public CableErrorType = CableErrorType;

    private partAmounts: PartAmounts;
    private inputDelay = 500;
    private inputDelayTimeout?: number;

    public constructor(
        @Inject(WIZARD_STEP_TOKEN)
        private injection: InjectionDataStep<CableConfigurationData>,
        private sparePartService: SparePartService,
        @Inject(WINDOW) private window: Window
    ) {
        super();
        this.partAmounts = this.injection.stepData.partAmounts;
    }

    public ngOnInit() {
        const spareParts = this.injection.stepData.parts;

        const sparePartIds = spareParts.map((sparePart) => sparePart.id);
        this.sparePartService
            .getCrossSellingCableSteps(sparePartIds)
            .pipe(takeUntil(this.destroySubject))
            .subscribe((cableStepsMetaData) => {
                cableStepsMetaData.forEach((cableStep) => {
                    const sparePart = spareParts.find((part) => part.id === cableStep.sparePartId);

                    if (!sparePart) {
                        return;
                    }

                    if (cableStep.steps === undefined) {
                        this.addCableConfigForUnavailableData(sparePart);

                        return;
                    }

                    this.addCableConfig(cableStep.steps, sparePart);
                });
                this.checkAllInputsValidity();
            });

        this.injection.nextStep.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            const basketItems: StoreStepItems = {};
            this.parts.forEach((part) => {
                basketItems[part.id] = [
                    {
                        basketItem: {
                            type: BasketItemType.CABLE,
                            productId: part.id,
                            quantity: this.partAmounts[(part as any).uniqueId],
                            length: part.currentCableValue,
                        },
                    },
                ];
            });
            this.injection.addBasketItems(basketItems);
        });
    }

    public decreaseCableSize(sparePart: SparePartWithCableSteps) {
        if (
            sparePart.cableError &&
            sparePart.cableErrorType === CableErrorType.UNAUTHORIZED_INTERMEDIATED_VALUE
        ) {
            const nextHigherValue = ensure(
                sparePart.cableSteps.find((step) => step > sparePart.currentCableValue)
            );
            const nextHigherIndex = sparePart.cableSteps.indexOf(nextHigherValue);
            sparePart.currentCableValue = sparePart.cableSteps[nextHigherIndex - 1];
            sparePart.currentCableStepIndex = nextHigherIndex - 1;
        } else {
            sparePart.currentCableStepIndex--;
            sparePart.currentCableValue = sparePart.cableSteps[sparePart.currentCableStepIndex];
        }
        sparePart.cableError = false;
        sparePart.cableErrorType = undefined;
        this.checkAllInputsValidity();
    }

    public increaseCableSize(sparePart: SparePartWithCableSteps) {
        if (
            sparePart.cableError &&
            sparePart.cableErrorType === CableErrorType.UNAUTHORIZED_INTERMEDIATED_VALUE
        ) {
            sparePart.currentCableValue = ensure(
                sparePart.cableSteps.find((step) => step > sparePart.currentCableValue)
            );
            sparePart.currentCableStepIndex = sparePart.cableSteps.indexOf(sparePart.currentCableValue);
        } else {
            sparePart.currentCableStepIndex++;
            sparePart.currentCableValue = sparePart.cableSteps[sparePart.currentCableStepIndex];
        }
        sparePart.cableError = false;
        sparePart.cableErrorType = undefined;
        this.checkAllInputsValidity();
    }

    public inputValue(part: SparePartWithCableSteps) {
        // value is null when user inputs e (euler's number) or if complete input is deleted
        if (part.currentCableValue === null || part.noCableDataAvailable) {
            this.checkAllInputsValidity();

            return;
        }

        if (this.inputDelayTimeout) {
            clearTimeout(this.inputDelayTimeout);
        }
        // Type 'Timeout' is not assignable to type 'number'.ts(2322)
        // https://stackoverflow.com/questions/55550096/ts2322-type-timeout-is-not-assignable-to-type-number-when-running-unit-te
        this.inputDelayTimeout = this.window.setTimeout(() => {
            this.processInputValue(part);
        }, this.inputDelay);
    }

    private processInputValue(part: SparePartWithCableSteps) {
        if (!part.cableSteps.includes(part.currentCableValue)) {
            part.cableError = true;
            if (part.currentCableValue < part.cableSteps[0]) {
                part.cableErrorMessageKey = 'X_SELLING_DIALOG.CABLE.ERROR_MINIMUM_EXCEEDED';
                part.cableErrorType = CableErrorType.MINIMUM_EXCEEDED;
                part.currentCableStepIndex = -1;
            } else if (part.currentCableValue > part.cableSteps[part.cableSteps.length - 1]) {
                part.cableErrorMessageKey = 'X_SELLING_DIALOG.CABLE.ERROR_MAXIMUM_EXCEEDED';
                part.cableErrorType = CableErrorType.MAXIMUM_EXCEEDED;
                part.currentCableStepIndex = part.cableSteps.length;
            } else {
                part.cableErrorMessageKey = 'X_SELLING_DIALOG.CABLE.ERROR_UNAUTHORIZED_INTERMEDIATED_VALUE';
                part.cableErrorType = CableErrorType.UNAUTHORIZED_INTERMEDIATED_VALUE;
            }
        } else {
            part.cableError = false;
            part.cableErrorType = undefined;
            part.currentCableStepIndex = part.cableSteps.indexOf(part.currentCableValue);
        }
        this.checkAllInputsValidity();
    }

    private calcCableSteps(steps: CrossSellingCableStep[]): number[] {
        const interval: number[] = [];
        let currentSize: number;
        steps.forEach((step, index) => {
            if (index === 0) {
                currentSize = step.min;
                while (roundFloatingNumber(currentSize + step.size) <= step.max) {
                    interval.push(currentSize);
                    currentSize = roundFloatingNumber(currentSize + step.size);
                }
                interval.push(step.max);
            } else {
                currentSize = roundFloatingNumber(step.min + step.size);
                while (currentSize < step.max) {
                    interval.push(currentSize);
                    currentSize = roundFloatingNumber(currentSize + step.size);
                }
                interval.push(step.max);
            }
        });

        return interval;
    }

    private checkAllInputsValidity() {
        if (!this.parts.find((part) => part.cableError || part.currentCableValue === null)) {
            this.injection.valid.next(true);
        } else {
            this.injection.valid.next(false);
        }
    }

    private addCableConfig(steps: CrossSellingCableStep[], sparePart: SparePart) {
        const cableSteps = this.calcCableSteps(steps);
        const cableIndex = cableSteps.indexOf(cableSteps[0]);

        this.parts.push({
            ...sparePart,
            cableSteps,
            currentCableStepIndex: cableIndex,
            currentCableValue: cableSteps[cableIndex],
            cableError: false,
            noCableDataAvailable: false,
        });
    }

    private addCableConfigForUnavailableData(sparePart: SparePart) {
        this.parts.push({
            ...sparePart,
            cableSteps: [],
            currentCableStepIndex: 0,
            currentCableValue: 1,
            cableError: false,
            noCableDataAvailable: true,
        });
    }
}
